/**
Author - Brijesh Pandey
Git - https://github.com/Brijesh-Pandey
**/

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import parse from 'html-react-parser';
import styled from 'styled-components';

import {ActionButton, Dropdown} from 'screens/commons';

import IMAGES from 'constants/Images';

const propTypes = {
  handleImageUpload: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  showAnimation: PropTypes.bool.isRequired,
  handleDeleteUploadedFile: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const UploadDocument = ({
  declarationsList,
  isLoading,
  isDisabled,
  handleImageUpload,
  uploadedFiles,
  showAnimation,
  handleDeleteUploadedFile,
  onSubmit,
  selectedOption,
  setSelectedOption,
  setPassword,
  password,
}) => {
  const [selectedOptionInfo, setSelectedOptionInfo] = useState(selectedOption?.text || '');
  const [isDragging, setIsDragging] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const dropdownOptions = declarationsList.allowedIncomeDeclarations?.map((eachDeclaration) => (
    <EachOption
      key={eachDeclaration.id || eachDeclaration.value}
      className="body-medium"
      onClick={() => {
        setSelectedOption(eachDeclaration);
        setSelectedOptionInfo(eachDeclaration?.text);
      }}
    >
      <p dangerouslySetInnerHTML={{__html: eachDeclaration.render}} />
    </EachOption>
  ));

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (acceptedFiles) => {
    setIsDragging(false);
    handleImageUpload(acceptedFiles);
  };

  return (
    <Wrapper>
      <SubTitle>Please choose a document type and upload it.</SubTitle>
      <CardContent>
        <>
          <InfoText>
            {selectedOptionInfo ? parse(selectedOptionInfo) : 'Select document type'}
          </InfoText>
          <Dropdown
            options={dropdownOptions}
            title={selectedOption?.render ? parse(selectedOption?.render) : 'Choose an option'}
            size="small"
          />
        </>
        <Dropzone
          onDrop={handleDrop}
          multiple={false}
          accept={{
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'application/pdf': ['.pdf'],
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {({getRootProps, getInputProps}) => (
            <DropZoneContainer
              {...getRootProps()}
              isDragging={isDragging}
              file={uploadedFiles.length > 0}
            >
              <FileInput {...getInputProps()} id="file-upload" />

              {uploadedFiles.length === 0 ? (
                showAnimation ? (
                  <DropzoneContent>
                    <span>Uploading...</span>
                  </DropzoneContent>
                ) : (
                  <DropzoneContent>
                    <MainUploadSection>
                      <UploadIconContainer>
                        <UploadIcon src={IMAGES.fileUploadImage} alt="Upload" />
                      </UploadIconContainer>
                      <TextContainer>
                        <UploadDescription>Drag 'n' drop the document here or</UploadDescription>
                        <UploadButton className="mt-3">Click to select files</UploadButton>
                      </TextContainer>
                    </MainUploadSection>
                  </DropzoneContent>
                )
              ) : (
                uploadedFiles.map((eachFile) => (
                  <FilePreview key={eachFile.id}>
                    <FileHeader>
                      <FileIcon src={IMAGES.fileIcon} alt="File" />
                      <FileName>{eachFile.name}</FileName>
                    </FileHeader>
                    <FileActions>
                      <FileButton
                        onClick={(e) => {
                          e.stopPropagation();
                          document.getElementById('file-upload').click();
                        }}
                      >
                        Change File
                      </FileButton>
                      <FileButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteUploadedFile(eachFile);
                        }}
                      >
                        Remove
                      </FileButton>
                    </FileActions>
                  </FilePreview>
                ))
              )}
            </DropZoneContainer>
          )}
        </Dropzone>
        <SupportedFormats>
          Supported file formats include only png, jpg, jpeg and pdf
        </SupportedFormats>
        <PasswordBox>
          <>
            <PasswordLabel className="mb-2">
              <LockIcon src={IMAGES.lockIcon} alt="Lock" />
              <PasswordText>
                Attention: Please provide password in the below input if it is a password-protected
                document.
              </PasswordText>
            </PasswordLabel>
            <PasswordInputContainer>
              <PasswordInput
                placeholder="Type your password here..."
                type={showPassword ? 'text' : 'password'}
                value={password || ''}
                onChange={(event) => setPassword(event.target.value)}
              />
              <TogglePasswordButton type="button" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? 'Hide' : 'Show'}
              </TogglePasswordButton>
            </PasswordInputContainer>
          </>
        </PasswordBox>
      </CardContent>

      <CardFooter>
        <ActionButton
          buttonText="Upload & Continue"
          callback={onSubmit}
          isLoading={isLoading}
          isDisabled={isDisabled}
          className="my-2"
        />
      </CardFooter>
    </Wrapper>
  );
};

// EachOption component for dropdown
const EachOption = styled.li`
  text-decoration: none;
  padding: 0.7rem;
  display: flex;
  align-items: center;
  color: ${(props) => props.theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  line-height: 1.5;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.background.secondary};
    color: ${(props) => props.theme.colors.brand.primary};
  }
`;

UploadDocument.propTypes = propTypes;

export default UploadDocument;

const Wrapper = styled.div`
  padding: 1rem 0;
  margin: 0 auto;
  height: 100%;

  .warning-text {
    color: #eb7134 !important;
    font-weight: 600;
  }
`;

const Title = styled.h1`
  margin: 0;
  padding: 0;
  font-size: ${(props) => props.theme.typography.fontSize.lg};
  font-weight: ${(props) => props.theme.typography.fontWeight.semiBold};
  color: ${(props) => props.theme.colors.text.primary};

  @media (max-width: 768px) {
    font-size: ${(props) => props.theme.typography.fontSize.lg};
  }
`;

const SubTitle = styled.p`
  margin: 0.5rem 0 0 1.5rem;
  padding: 0;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.text.secondary};
`;

const CardContent = styled.div`
  padding: 1.5rem 2rem 2rem;

  @media (min-width: 768px) {
    padding: 1.5rem 2.4rem 2.4rem;
  }
`;

const CardFooter = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 2rem 2rem;
`;

const InfoText = styled.p`
  margin: 0.5rem 0;
  margin-top: 1rem;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.brand.primary};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
`;

const DropZoneContainer = styled.div`
  border: 2px dashed ${(props) => (props.isDragging ? props.theme.colors.brand.primary : '#ddc9f4')};
  border-radius: 0.8rem;
  background-color: #f8fafc;

  padding: 2.4rem;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  margin: 1.6rem 0 0 0;
  &:hover {
    border-color: #a6a6a6b5;
  }
`;

const DropzoneContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 1.6rem;
`;

const MainUploadSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
`;

const TextContainer = styled.div`
  text-align: left;
`;

const UploadIconContainer = styled.div`
  width: 6.4rem;
  height: 6.4rem;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.brand.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UploadIcon = styled.img`
  width: 3.2rem;
  height: 3.2rem;
`;

const UploadButton = styled.button`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  padding: 0.8rem 1.6rem;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  margin-bottom: 0.8rem;
  display: inline-block;
  text-align: center;

  &:hover {
    background-color: #e2e8f0;
  }
`;

const UploadDescription = styled.p`
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.text.secondary};
  margin: 0;
`;

const FileInfoBox = styled.div`
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  padding: 2rem 3rem;
  margin: 0 auto;
  text-align: center;
  width: 100%;
  &:hover {
    background-color: #e2e8f0;
  }
`;

const FileInfoText = styled.p`
  margin: 0.4rem 0;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.text.primary};
`;

const FilePreview = styled.div`
  width: 100%;
`;

const FileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.6rem;
`;

const FileIcon = styled.img`
  width: 2.4rem;
  height: 2.4rem;
`;

const FileName = styled.span`
  font-size: ${(props) => props.theme.typography.fontSize.md};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.text.primary};
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  flex: 1;
  text-align: left;
`;

const FileActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
`;

const FileButton = styled.button`
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  padding: 0.8rem 1.6rem;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.8rem;

  &:hover {
    background-color: #f8fafc;
  }
`;

const PasswordBox = styled.div`
  margin: 1.6rem 0;
  background-color: ${(props) => props.theme.colors.background.secondary};
  padding: 1rem;
  border-radius: 0.6rem;
`;

const PasswordLabel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
`;

const LockIcon = styled.img`
  width: 1.2rem;
  height: 1.2rem;
  margin-top: 0.2rem;
`;

const PasswordText = styled.p`
  margin: 0;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.brand.tertiary};
`;

const PasswordInputContainer = styled.div`
  position: relative;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  color: ${(props) => props.theme.colors.text.primary};
  outline: none;
  transition: all 0.2s ease-in-out;

  &::placeholder {
    color: ${(props) => props.theme.colors.text.secondary};
  }

  &:focus {
    border-color: ${(props) => props.theme.colors.brand.primary};
    box-shadow: 0 0 0 2px ${(props) => `${props.theme.colors.brand.primary}1A`};
  }
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  padding: 0 1.2rem;
  background: none;
  border: none;
  cursor: pointer;
`;

const SupportedFormats = styled.p`
  font-size: ${(props) => props.theme.typography.fontSize.xs};
  color: ${(props) => props.theme.colors.text.secondary};
  margin: 0.2rem 0 0 0;
`;

const FileInput = styled.input`
  display: none;
`;
